<template>
  <div
    class="radar-grid-object-list-item flex flex-row justify-start items-center gap-2 p-2 aisea-text-14-400-white"
    :class="{
      danger: isObjectDanger,
      caution: isObjectCaution,
      safe: isObjectSafe,
      manual: hasManualAssessment,
    }"
    @click.stop.prevent="_handleClick"
  >
    <div class="obj-icon flex items-center">
      <img :src="radarObjectImgSrc" />
    </div>
    <div class="obj-name">
      {{ trackNumber }}
    </div>
    <div class="obj-elapsed-time ml-auto">
      {{ elapsedTime }}
    </div>
  </div>
</template>

<script>
import { RadarGridMixin } from "@/mixins";
import debounce from "lodash/debounce";
import isNumber from "lodash/isNumber";
import { DateUtil } from "@/utils";
const trackImage = require("@/assets/images/radarGrid/track.svg");
const targetImage = require("@/assets/images/radarGrid/target.svg");

export default {
  name: "RgObjectListItem",
  mixins: [RadarGridMixin],
  props: {
    trackNumber: { type: String, required: true },
    kind: { type: String, required: false },
    timestamp: {
      type: Object, // firebase.firestore.Timestamp
      required: false,
    },
    pictures: {
      type: Array, // Array<RadarGridObjectFirestoreData.p>
      required: false,
    },
    handleClick: {
      type: Function,
      required: true,
    },
    timeCount: {
      type: Number,
      required: true,
    },
    rgRiskLevelManager: {
      type: Object, // RgRiskLevelManager
      required: true,
    },
  },
  data() {
    return {
      elapsedTime: "---",
    };
  },
  watch: {
    timeCount() {
      this.elapsedTime = this.getElapsedTime();
    },
  },
  computed: {
    radarObjectImgSrc() {
      if (this.isTracked(this.kind)) return trackImage;
      return targetImage;
    },
    radarObjectTimestamp() {
      if (!isNumber(this.timestamp)) return this.pictureTimestamp;
      return this.timestamp;
    },
    pictureTimestamp() {
      try {
        return this.pictures[0].t.toMillis();
      } catch (e) {
        return null;
      }
    },
    isObjectCaution() {
      return this.isCaution(this.riskLevel);
    },
    isObjectDanger() {
      return this.isDanger(this.riskLevel);
    },
    isObjectSafe() {
      return this.isSafe(this.riskLevel);
    },
    hasManualAssessment() {
      if (!this.rgRiskLevelManager) return false;
      return this.rgRiskLevelManager.hasManualAssessment;
    },
    riskLevel() {
      if (!this.rgRiskLevelManager) return this.RG_RISK_LEVEL.CAUTION;
      return this.rgRiskLevelManager.riskLevel;
    },
  },
  methods: {
    setup() {
      this.elapsedTime = this.getElapsedTime();
    },
    _handleClick: debounce(
      function () {
        this.handleClick();
      },
      300,
      { leading: true, trailing: false },
    ),
    getElapsedTime() {
      if (!isNumber(this.radarObjectTimestamp)) return "---";
      return DateUtil.elapsedTimeWithMinimumUnitMinute(
        this.radarObjectTimestamp,
      );
    },
  },
  mounted() {
    this.setup();
  },
  beforeDestroy() {},
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.radar-grid-object-list-item {
  --danger-color: #892828;
  --danger-hover-color: #ae3030;
  --danger-border-color: #ad2f2f;
  --caution-color: #818316;
  --caution-hover-color: #aeb119;
  --caution-border-color: #aeb119;
  --safe-color: #146336;
  --safe-hover-color: #168545;
  --safe-border-color: #168545;
  --manual-border-width: 1px;

  cursor: pointer;
  border-radius: 5px;
  width: 256px;
  height: 44px;

  background-color: var(--danger-color);
  border-color: var(--danger-border-color);
  &:hover {
    background-color: var(--danger-hover-color);
  }

  &.danger {
    background-color: var(--danger-color);
    border-color: var(--danger-border-color);
    &:hover {
      background-color: var(--danger-hover-color);
    }
  }
  &.caution {
    background-color: var(--caution-color);
    border-color: var(--caution-border-color);
    &:hover {
      background-color: var(--caution-hover-color);
    }
  }
  &.safe {
    background-color: var(--safe-color);
    border-color: var(--safe-border-color);
    &:hover {
      background-color: var(--safe-hover-color);
    }
  }
  &.manual {
    border-style: solid;
    border-width: var(--manual-border-width);
    opacity: 0.5;
    padding: calc(0.5rem - var(--manual-border-width));
  }
}
</style>
